import React, { useState, useEffect } from 'react';
import { useSnackbar } from 'notistack';
import { capitalize } from '@mui/material/utils';
import {
  Box,
  Button,
  Table,
  TableBody,
  TableRow,
  Tooltip,
  Typography
} from '@mui/material';
import ArrowForwardIcon from '@mui/icons-material/ArrowForward';
import LinkIcon from '@mui/icons-material/Link';
import LinkOffIcon from '@mui/icons-material/LinkOff';
import moment from 'moment';
import { formDataInstance } from '@/common/axios';
import CONFIG from '@/config';
import { getRestHeaders } from '@/common/utils/api';
import CardLoader from '@/components/CardLoader';
import PatientUpdateConfirmModal from './PatientUpdateConfirmModal';
import { LoadingButton } from '@mui/lab';
import {
  AMDLinkStyles,
  AMDLinkTableCell,
  AMDLinkCard,
  AMDLinkBlueText
} from './index';
import { capitalizeAll } from '@/common/utils/utils';

const AdvancedMDProfile = (props: any) => {
  const { customerData, patientData, navigateProfile, setPatient } = props;
  const { enqueueSnackbar } = useSnackbar();
  const classes = AMDLinkStyles();

  const [loadingEncounters, setLoadingEncounters] = useState(false);
  const [loadingPatientUpdate, setLoadingPatientUpdate] = useState(false);
  const [loadingPatientLink, setLoadingPatientLink] = useState(false);
  //const [patient, setPatient] = useState(patientData);
  const [AMDEncounters, setAMDEncounters] = useState([]);
  const [isUpdatePatientModalOpen, setIsUpdatePatientModalOpen] =
    useState(false);
  const linkStatus = patientData?.linkedCustomerId
    ? patientData.linkedCustomerId === customerData.id
      ? 'linked'
      : 'conflict'
    : 'unlinked';

  const getAMDEncounters = async () => {
    try {
      setLoadingEncounters(true);
      const response = await formDataInstance.get(
        CONFIG.MEDICAL_API_URL + '/staff/amd-medical-encounters/',
        {
          params: { patientId: patientData?.patientId },
          headers: getRestHeaders()
        }
      );
      setAMDEncounters(response?.data?.data);
    } catch (error) {
      enqueueSnackbar('Error fetching AMD encounters', {
        variant: 'error'
      });
    } finally {
      setLoadingEncounters(false);
    }
  };

  const handleUpdatePatient = async () => {
    try {
      setLoadingPatientUpdate(true);
      const response = await formDataInstance.patch(
        CONFIG.MEDICAL_API_URL + '/staff/patients/',
        {
          customerId: customerData.id
        },
        {
          headers: getRestHeaders()
        }
      );
      setPatient({
        ...response?.data?.data,
        linkedCustomerId: patientData?.linkedCustomerId
      });
      enqueueSnackbar('Patient updated successfully', {
        variant: 'success'
      });
    } catch (error) {
      enqueueSnackbar('Error updating patient', {
        variant: 'error'
      });
    } finally {
      setLoadingPatientUpdate(false);
      setIsUpdatePatientModalOpen(false);
    }
  };

  const handleLinkPatient = async () => {
    try {
      setLoadingPatientLink(true);
      const method = customerData?.linkStatus === 'linked' ? 'patch' : 'post';
      const url = CONFIG.MEDICAL_API_URL + '/staff/customer-patient-link/';
      const data = {
        patientId: patientData?.patientId,
        customerId: customerData.id
      };
      const config = {
        headers: getRestHeaders()
      };

      await formDataInstance({
        method: method,
        url: url,
        data: data,
        ...config
      });

      enqueueSnackbar('Patient linked successfully', {
        variant: 'success'
      });
      navigateProfile(customerData.id);
    } catch (error) {
      enqueueSnackbar('Error linking patient', {
        variant: 'error'
      });
    } finally {
      setLoadingPatientLink(false);
    }
  };

  useEffect(() => {
    if (patientData) {
      getAMDEncounters();
    } else {
      setAMDEncounters([]);
    }
  }, [patientData]);

  return (
    <>
      <Box pt={2} pr={2} pl={2}>
        <Typography className={classes.columnHeader}>
          AdvancedMD patient details
        </Typography>
        {patientData ? (
          <>
            <Box
              sx={{
                display: 'inline-flex',
                alignItems: 'center',
                mb: 2
              }}
            >
              {linkStatus === 'linked' && (
                <Tooltip title="Linked">
                  <LinkIcon
                    sx={{
                      color: AMDLinkBlueText,
                      mr: 1
                    }}
                  />
                </Tooltip>
              )}
              {linkStatus === 'conflict' && (
                <Tooltip title="Conflicting Link">
                  <LinkOffIcon sx={{ color: 'red', mr: 1 }} />
                </Tooltip>
              )}
              <Typography className={classes.amdNameText}>
                {patientData?.firstName} {patientData?.lastName}
              </Typography>
            </Box>
            <Table sx={{ mb: 2 }}>
              <TableBody>
                <TableRow>
                  <AMDLinkTableCell align="left">
                    <Typography className={classes.strongText}>
                      Date of birth
                    </Typography>
                  </AMDLinkTableCell>
                  <AMDLinkTableCell align="right">
                    <Typography className={classes.text}>
                      {patientData?.dob}
                    </Typography>
                  </AMDLinkTableCell>
                </TableRow>
                <TableRow>
                  <AMDLinkTableCell align="left">
                    <Typography className={classes.strongText}>Sex</Typography>
                  </AMDLinkTableCell>
                  <AMDLinkTableCell align="right">
                    <Typography className={classes.text}>
                      {capitalize(patientData?.sex)}
                    </Typography>
                  </AMDLinkTableCell>
                </TableRow>
                <TableRow>
                  <AMDLinkTableCell align="left">
                    <Typography className={classes.strongText}>
                      Phone
                    </Typography>
                  </AMDLinkTableCell>
                  <AMDLinkTableCell align="right">
                    <Typography className={classes.text}>
                      {patientData?.phone}
                    </Typography>
                  </AMDLinkTableCell>
                </TableRow>
                <TableRow>
                  <AMDLinkTableCell align="left">
                    <Typography className={classes.strongText}>
                      Email
                    </Typography>
                  </AMDLinkTableCell>
                  <AMDLinkTableCell align="right">
                    <Typography className={classes.text}>
                      {patientData?.email?.toLowerCase()}
                    </Typography>
                  </AMDLinkTableCell>
                </TableRow>
              </TableBody>
            </Table>
            <Box sx={{ mb: 3 }}>
              {linkStatus === 'unlinked' && (
                <LoadingButton
                  variant="contained"
                  color="primary"
                  loading={loadingPatientLink}
                  onClick={handleLinkPatient}
                >
                  Link to POW profile
                </LoadingButton>
              )}
              {linkStatus === 'linked' && (
                <Button
                  variant="outlined"
                  color="primary"
                  onClick={() => setIsUpdatePatientModalOpen(true)}
                >
                  Update AdvancedMD profile
                </Button>
              )}
              {linkStatus === 'conflict' && (
                <Box
                  sx={{
                    display: 'inline-flex',
                    alignItems: 'center',
                    cursor: 'pointer'
                  }}
                  onClick={() => navigateProfile(patientData?.linkedCustomerId)}
                  className={classes.strongText}
                >
                  <Typography color={AMDLinkBlueText}>
                    View linked client profile
                  </Typography>
                  <ArrowForwardIcon sx={{ ml: 0.5, color: AMDLinkBlueText }} />
                </Box>
              )}
            </Box>
            <Typography className={classes.strongText} sx={{ mb: 0.5 }}>
              Recent AdvancedMD encounters
            </Typography>
            <Box
              style={{
                overflow: 'auto',
                height: 'calc(100vh - 430px)',
                paddingRight: '5px'
              }}
            >
              {loadingEncounters ? (
                <div style={{ marginTop: '40px' }}>
                  <CardLoader />
                </div>
              ) : AMDEncounters.length > 0 ? (
                AMDEncounters.map((encounter, index) => {
                  return (
                    <AMDLinkCard key={index} sx={{ mt: 2, p: 2 }} elevation={0}>
                      <Typography className={classes.lightText}>
                        {moment(encounter?.timestamp).format('MM/DD/YYYY')}
                      </Typography>
                      <Typography className={classes.lightText}>
                        {moment(encounter?.timestamp).format('h:mm A')}
                      </Typography>
                      <Typography className={classes.lightText} sx={{ mb: 1 }}>
                        {encounter?.storeDescription}
                      </Typography>
                      <Typography className={classes.strongText} sx={{ mb: 1 }}>
                        {capitalizeAll(encounter?.type.replace(/_/g, ' '))}
                      </Typography>
                      {encounter?.purchasableItems &&
                        encounter?.purchasableItems.map((item, i) => (
                          <Typography key={i} className={classes.text}>
                            {item?.description}
                          </Typography>
                        ))}
                      {encounter?.type == 'medical_call' && (
                        <>
                          <Typography className={classes.text}>
                            <b>Contact Purpose:</b> {encounter?.contactPurpose}
                          </Typography>
                          <Typography className={classes.text}>
                            <b>Contact Type:</b> {encounter?.contactType}
                          </Typography>
                          <Typography className={classes.text}>
                            <b>Nurse Practitioner:</b>{' '}
                            {encounter?.nursePractitionerName}
                          </Typography>
                        </>
                      )}
                    </AMDLinkCard>
                  );
                })
              ) : (
                <Typography className={classes.lightText}>
                  No AMD encounters found.
                </Typography>
              )}
            </Box>
          </>
        ) : (
          <Typography className={classes.lightText}>
            Select from the search results to view the AdvancedMD profile, or
            create a new profile.
          </Typography>
        )}
      </Box>
      <PatientUpdateConfirmModal
        isOpen={isUpdatePatientModalOpen}
        onClose={() => setIsUpdatePatientModalOpen(false)}
        onConfirm={handleUpdatePatient}
        loading={loadingPatientUpdate}
      />
    </>
  );
};

export default AdvancedMDProfile;
